import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  FiShield, 
  FiPlus, 
  FiRefreshCw, 
  FiAlertTriangle, 
  FiCheckCircle, 
  FiExternalLink,
  FiInbox,
  FiTrash2,
  FiX,
  FiLayers,
  FiActivity,
  FiCheck,
  FiSliders,
  FiFileText,
  FiCheckSquare
} from "react-icons/fi";
import { fmeaApi, type FmeaRecordDto, type FmeaCreateRequest } from "../../../services/fmeaApi.js";
import { rcaApi } from "../../../services/rcaApi.js";
import { complaintApi } from "../../../services/complaintApi.js";
import { CmtsEnterpriseModal } from "../../CmtsEnterpriseModal.js";

function calculateActionPriority(s: number, o: number, d: number): { ap: "HIGH" | "MEDIUM" | "LOW"; color: string; badge: string } {
  // AIAG & VDA AP Logic Approximation
  if (s >= 9 || (s >= 7 && o >= 7) || (s >= 7 && d >= 7)) {
    return { ap: "HIGH", color: "bg-rose-50 text-rose-700 border-rose-200", badge: "bg-rose-600 text-white" };
  }
  if (s >= 6 || (o >= 5 && d >= 5) || (s >= 4 && o >= 6)) {
    return { ap: "MEDIUM", color: "bg-amber-50 text-amber-700 border-amber-200", badge: "bg-amber-500 text-white" };
  }
  return { ap: "LOW", color: "bg-emerald-50 text-emerald-700 border-emerald-200", badge: "bg-emerald-600 text-white" };
}

export function RcaFmeaPage() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  // Form State
  const [selectedComplaintId, setSelectedComplaintId] = useState<string>("");
  const [selectedRcaId, setSelectedRcaId] = useState<string>("");
  const [fmeaType, setFmeaType] = useState<string>("PFMEA");
  const [revision, setRevision] = useState<string>("Rev 1");
  const [processOrItem, setProcessOrItem] = useState<string>("");
  const [failureMode, setFailureMode] = useState<string>("");
  const [potentialCause, setPotentialCause] = useState<string>("");
  const [severity, setSeverity] = useState<number>(8);
  const [occurrence, setOccurrence] = useState<number>(5);
  const [detection, setDetection] = useState<number>(6);
  const [status, setStatus] = useState<string>("ACTIVE");
  const [syncFeedback, setSyncFeedback] = useState<boolean>(true);

  // Live FMEA Query from Database
  const fmeaQuery = useQuery({
    queryKey: ["cmts", "fmea", "queue"],
    queryFn: () => fmeaApi.queue(),
    retry: 1,
  });

  // Complaints Query for dropdown
  const complaintsQuery = useQuery({
    queryKey: ["cmts", "complaints", "list-for-fmea"],
    queryFn: () => complaintApi.list({ pageSize: 50 }),
    retry: 1,
  });

  // RCAs Query for dropdown
  const rcasQuery = useQuery({
    queryKey: ["cmts", "rca", "queue-for-fmea"],
    queryFn: () => rcaApi.queue(),
    retry: 1,
  });

  const records: readonly FmeaRecordDto[] = fmeaQuery.data || [];
  const complaints = complaintsQuery.data?.items || [];
  const rcas = rcasQuery.data || [];

  // Auto-increment Doc Number calculation (e.g. PFMEA-2026-0002)
  const autoGeneratedDocNo = useMemo(() => {
    let maxSeq = records.length;
    records.forEach(r => {
      const match = r.fmeaDocNo?.match(/(\d+)$/);
      if (match && match[1]) {
        const num = parseInt(match[1], 10);
        if (!isNaN(num) && num > maxSeq) maxSeq = num;
      }
    });
    const prefix = fmeaType === "DFMEA" ? "DFMEA" : (fmeaType === "FMEA-MSR" ? "FMEA-MSR" : "PFMEA");
    return `${prefix}-2026-${String(maxSeq + 1).padStart(4, "0")}`;
  }, [records, fmeaType]);

  // Filter RCAs for currently selected complaint
  const eligibleRcas = useMemo(() => {
    if (!selectedComplaintId) return rcas;
    return rcas.filter(r => r.complaintId === selectedComplaintId);
  }, [rcas, selectedComplaintId]);

  // Create Mutation
  const createMutation = useMutation({
    mutationFn: (payload: FmeaCreateRequest) => fmeaApi.create(payload),
    onSuccess: (newRecord) => {
      queryClient.invalidateQueries({ queryKey: ["cmts", "fmea", "queue"] });
      setIsModalOpen(false);
      setSaveSuccessMsg(`FMEA Record "${newRecord.fmeaDocNo}" created and saved to database successfully!`);
      setTimeout(() => setSaveSuccessMsg(null), 5000);
      resetForm();
    },
    onError: (err: any) => {
      setLocalError(err?.message || "Failed to save FMEA record to database.");
    }
  });

  // Delete Mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => fmeaApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cmts", "fmea", "queue"] });
    }
  });

  const resetForm = () => {
    setSelectedComplaintId("");
    setSelectedRcaId("");
    setFmeaType("PFMEA");
    setRevision("Rev 1");
    setProcessOrItem("");
    setFailureMode("");
    setPotentialCause("");
    setSeverity(8);
    setOccurrence(5);
    setDetection(6);
    setStatus("ACTIVE");
    setSyncFeedback(true);
    setLocalError(null);
  };

  const handleOpenModal = () => {
    resetForm();
    if (complaints.length > 0 && complaints[0]) {
      const cmp = complaints[0];
      setSelectedComplaintId(cmp.id);
      setProcessOrItem(cmp.title || "Production Process");
      setFailureMode(cmp.title || "Dimensional / Functional Non-conformance");
    }
    setIsModalOpen(true);
  };

  const handleComplaintChange = (cmpId: string) => {
    setSelectedComplaintId(cmpId);
    const cmp = complaints.find(c => c.id === cmpId);
    if (cmp) {
      setProcessOrItem(cmp.title || "Production Process");
      setFailureMode(cmp.title || "Dimensional / Functional Non-conformance");
    }
    // Auto-select linked RCA if available
    const linkedRca = rcas.find(r => r.complaintId === cmpId);
    if (linkedRca) {
      setSelectedRcaId(linkedRca.id);
      if (linkedRca.rootCause) {
        setPotentialCause(linkedRca.rootCause);
      }
    } else {
      setSelectedRcaId("");
    }
  };

  const handleRcaChange = (rcaId: string) => {
    setSelectedRcaId(rcaId);
    const rca = rcas.find(r => r.id === rcaId);
    if (rca) {
      if (rca.rootCause) setPotentialCause(rca.rootCause);
      if (rca.problemStatement && !failureMode) setFailureMode(rca.problemStatement);
    }
  };

  const handleSave = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setLocalError(null);

    if (!selectedComplaintId) {
      setLocalError("Please select a Complaint to link.");
      return;
    }
    if (!processOrItem.trim() || !failureMode.trim()) {
      setLocalError("Please enter Process Step/Item and Failure Mode.");
      return;
    }

    const apObj = calculateActionPriority(severity, occurrence, detection);

    const payload: FmeaCreateRequest = {
      complaintId: selectedComplaintId,
      rcaId: selectedRcaId || null,
      fmeaDocNo: autoGeneratedDocNo,
      fmeaType,
      revision,
      processOrItem: processOrItem.trim(),
      failureMode: failureMode.trim(),
      potentialCause: potentialCause.trim() || undefined,
      severity,
      occurrence,
      detection,
      actionPriority: apObj.ap,
      status,
      targetDate: new Date(Date.now() + 14 * 86400000).toISOString(),
    };

    createMutation.mutate(payload);
  };

  // Live calculation for modal preview
  const liveAp = useMemo(() => calculateActionPriority(severity, occurrence, detection), [severity, occurrence, detection]);
  const liveRpn = severity * occurrence * detection;

  // Stats
  const stats = useMemo(() => {
    const total = records.length;
    const highApCount = records.filter(r => r.actionPriority === "HIGH").length;
    const rcaLinkedCount = records.filter(r => Boolean(r.rcaId)).length;
    const activeCount = records.filter(r => r.status === "ACTIVE" || r.status === "UPDATE_IN_PROGRESS").length;
    return { total, highApCount, rcaLinkedCount, activeCount };
  }, [records]);

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded-full bg-blue-50 text-blue-700 border border-blue-200">
              Risk Management
            </span>
            <span className="text-xs text-slate-400 font-medium">AIAG & VDA Harmonized</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mt-1.5 flex items-center gap-2">
            <FiShield className="w-6 h-6 text-blue-600" />
            FMEA Risk Registry & Feedback Loop
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Governed Failure Mode and Effects Analysis linking complaints and root-cause investigations directly to the PostgreSQL database.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => queryClient.invalidateQueries({ queryKey: ["cmts", "fmea", "queue"] })}
            className="px-3.5 py-2 border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold text-xs rounded-lg shadow-sm flex items-center gap-1.5 transition-colors cursor-pointer"
          >
            <FiRefreshCw className={`w-3.5 h-3.5 ${fmeaQuery.isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>

          <button
            type="button"
            onClick={handleOpenModal}
            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-2 transition-all transform hover:-translate-y-0.5 cursor-pointer"
          >
            <FiPlus className="w-4 h-4" />
            Create FMEA Record
          </button>
        </div>
      </div>

      {/* Success Alert Banner */}
      {saveSuccessMsg && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-xs font-semibold text-emerald-800 flex items-center gap-2 shadow-sm animate-fade-in">
          <FiCheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <span>{saveSuccessMsg}</span>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total FMEA Records</span>
          <div className="text-2xl font-bold text-slate-900 mt-1">{stats.total}</div>
          <span className="text-[11px] text-slate-500 mt-1 block">Registered in PostgreSQL</span>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">High Action Priority (AP)</span>
          <div className="text-2xl font-bold text-rose-700 mt-1">{stats.highApCount}</div>
          <span className="text-[11px] text-rose-600 font-semibold mt-1 block">Requires process mitigation</span>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">RCA Linked Records</span>
          <div className="text-2xl font-bold text-blue-600 mt-1">{stats.rcaLinkedCount}</div>
          <span className="text-[11px] text-slate-500 mt-1 block">Linked with active RCAs</span>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Active Status</span>
          <div className="text-2xl font-bold text-sky-700 mt-1">{stats.activeCount}</div>
          <span className="text-[11px] text-sky-600 font-semibold mt-1 block">Under review / update</span>
        </div>
      </div>

      {/* FMEA Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Database Risk Registry Records</h3>
            <p className="text-xs text-slate-500">Live AIAG & VDA risk records mapped to complaints</p>
          </div>
        </div>

        {records.length === 0 ? (
          <div className="p-12 text-center space-y-3">
            <FiInbox className="w-10 h-10 text-slate-300 mx-auto" />
            <h4 className="text-sm font-bold text-slate-700">No FMEA Records in Database</h4>
            <p className="text-xs text-slate-400 max-w-sm mx-auto">
              There are no FMEA records registered yet. Click below to create and link an FMEA record to a complaint.
            </p>
            <button
              type="button"
              onClick={handleOpenModal}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg shadow-sm inline-flex items-center gap-1.5 mt-2 cursor-pointer"
            >
              <FiPlus className="w-4 h-4" /> Create FMEA Record
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200 uppercase text-[11px]">
                <tr>
                  <th className="py-3.5 px-4">FMEA Document</th>
                  <th className="py-3.5 px-3">Complaint</th>
                  <th className="py-3.5 px-3">Process / Item</th>
                  <th className="py-3.5 px-4 max-w-xs">Failure Mode / Cause</th>
                  <th className="py-3.5 px-3 text-center">S · O · D</th>
                  <th className="py-3.5 px-3 text-center">RPN</th>
                  <th className="py-3.5 px-3">Action Priority</th>
                  <th className="py-3.5 px-3">Origin RCA</th>
                  <th className="py-3.5 px-3">Status</th>
                  <th className="py-3.5 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {records.map((row) => {
                  const apObj = calculateActionPriority(row.severity, row.occurrence, row.detection);
                  return (
                    <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-3.5 px-4 font-bold text-blue-600 whitespace-nowrap">
                        {row.fmeaType} — {row.fmeaDocNo}
                        <div className="text-[10px] text-slate-400 font-normal">{row.revision}</div>
                      </td>
                      <td className="py-3.5 px-3 font-medium text-slate-800 whitespace-nowrap">
                        <div className="font-semibold text-slate-900">{row.complaintCode || "CMP-001"}</div>
                        {row.complaintTitle && (
                          <div className="text-[11px] text-slate-500 max-w-[130px] truncate">{row.complaintTitle}</div>
                        )}
                      </td>
                      <td className="py-3.5 px-3 font-semibold text-slate-900 whitespace-nowrap">
                        {row.processOrItem}
                      </td>
                      <td className="py-3.5 px-4 text-slate-700 max-w-xs">
                        <div className="font-medium text-slate-900 truncate">{row.failureMode}</div>
                        {row.potentialCause && (
                          <div className="text-[11px] text-slate-500 truncate">Cause: {row.potentialCause}</div>
                        )}
                      </td>
                      <td className="py-3.5 px-3 text-center font-mono font-bold whitespace-nowrap">
                        {row.severity} · {row.occurrence} · {row.detection}
                      </td>
                      <td className="py-3.5 px-3 text-center font-mono font-bold text-slate-800">
                        {row.rpn || row.severity * row.occurrence * row.detection}
                      </td>
                      <td className="py-3.5 px-3 whitespace-nowrap">
                        <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${apObj.color}`}>
                          {row.actionPriority || apObj.ap} AP
                        </span>
                      </td>
                      <td className="py-3.5 px-3 font-bold text-blue-600 whitespace-nowrap">
                        {row.rcaCode || "-"}
                      </td>
                      <td className="py-3.5 px-3 whitespace-nowrap">
                        <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-sky-50 text-sky-700 border border-sky-200">
                          {row.status}
                        </span>
                      </td>
                      <td className="py-3.5 px-3 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Delete FMEA record "${row.fmeaDocNo}"?`)) {
                              deleteMutation.mutate(row.id);
                            }
                          }}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors cursor-pointer"
                          title="Delete FMEA Record"
                        >
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* =========================================================================
          Sleek Modern Create FMEA Risk Record Modal (Exact Image 2 Style)
         ========================================================================= */}
      <CmtsEnterpriseModal
        open={isModalOpen}
        title="Create FMEA Risk Record"
        subtitle="Configure AIAG & VDA Failure Mode and Effects Analysis risk parameters and database linkage."
        size="lg"
        onClose={() => setIsModalOpen(false)}
        footer={
          <div className="cmts-job-modal-footer">
            <button
              type="button"
              className="cmts-job-modal-btn cmts-job-modal-btn--secondary cursor-pointer"
              disabled={createMutation.isPending}
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>

            <button
              type="button"
              className="cmts-job-modal-btn cmts-job-modal-btn--primary cursor-pointer"
              disabled={createMutation.isPending}
              onClick={() => handleSave()}
            >
              {createMutation.isPending ? "Saving to Database..." : "Create FMEA Record"}
            </button>
          </div>
        }
      >
        <div className="cmts-job-form">
          {localError && (
            <div
              style={{
                padding: "0.65rem 0.9rem",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 7,
                color: "#991b1b",
                fontSize: "0.8rem",
                fontWeight: 600,
              }}
            >
              {localError}
            </div>
          )}

          <div className="cmts-job-form__grid">
            <label>
              Select Complaint *
              <select
                value={selectedComplaintId}
                onChange={(e) => handleComplaintChange(e.target.value)}
                required
              >
                <option value="">-- Choose Complaint --</option>
                {complaints.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.complaintCode || c.id.slice(0, 8)} — {c.title}
                  </option>
                ))}
              </select>
              <small>Unique complaint identifier for backend risk linkage</small>
            </label>

            <label>
              Linked RCA Investigation (Optional)
              <select
                value={selectedRcaId}
                onChange={(e) => handleRcaChange(e.target.value)}
              >
                <option value="">-- None / Standalone --</option>
                {eligibleRcas.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.rcaCode} — {r.rcaMethodName || "RCA"} ({r.rootCause ? "Verified" : "In Progress"})
                  </option>
                ))}
              </select>
              <small>Confirmed root cause investigation mapping</small>
            </label>

            <label>
              FMEA Type *
              <select
                value={fmeaType}
                onChange={(e) => setFmeaType(e.target.value)}
              >
                <option value="PFMEA">Process FMEA (PFMEA)</option>
                <option value="DFMEA">Design FMEA (DFMEA)</option>
                <option value="FMEA-MSR">FMEA-MSR (Monitoring)</option>
              </select>
              <small>Auto-allocates next document sequence ({autoGeneratedDocNo})</small>
            </label>

            <label>
              Revision *
              <select
                value={revision}
                onChange={(e) => setRevision(e.target.value)}
              >
                <option value="Rev 1">Rev 1</option>
                <option value="Rev 2">Rev 2</option>
                <option value="Rev 3">Rev 3</option>
                <option value="Rev 4">Rev 4</option>
                <option value="Rev 5">Rev 5</option>
              </select>
              <small>Document revision lifecycle version</small>
            </label>

            <label style={{ gridColumn: "span 2" }}>
              Process Step / Component Item *
              <input
                type="text"
                value={processOrItem}
                onChange={(e) => setProcessOrItem(e.target.value)}
                placeholder="e.g. CNC Milling & Dimension Control, Die-casting Aluminum Housing"
                required
              />
              <small>Manufacturing process step, station, or mechanical component item</small>
            </label>

            <label>
              Potential Failure Mode *
              <input
                type="text"
                value={failureMode}
                onChange={(e) => setFailureMode(e.target.value)}
                placeholder="e.g. Dimension Deviation (+0.08mm) or Porosity"
                required
              />
              <small>Observed or potential non-conformance physical manifestation</small>
            </label>

            <label>
              Potential Cause / Mechanism
              <input
                type="text"
                value={potentialCause}
                onChange={(e) => setPotentialCause(e.target.value)}
                placeholder="e.g. Tool wear, coolant contamination, thermal drift"
              />
              <small>Physical or operational root-cause mechanism</small>
            </label>

            <label style={{ gridColumn: "span 2" }}>
              FMEA Status *
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="UPDATE_IN_PROGRESS">UPDATE IN PROGRESS</option>
                <option value="APPROVED">APPROVED</option>
                <option value="COMPLETED">COMPLETED</option>
              </select>
              <small>Current operational lifecycle governance state</small>
            </label>
          </div>

          {/* AIAG & VDA Risk Evaluation (S · O · D Scoring Box) */}
          <div style={{
            padding: "1rem",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            marginTop: "0.25rem"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
              <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#1e293b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                AIAG & VDA Risk Evaluation (S · O · D)
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>RPN: {liveRpn}</span>
                <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${liveAp.color}`}>
                  {liveAp.ap} Action Priority
                </span>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "1rem" }}>
              {/* Severity */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.72rem", fontWeight: 700, color: "#334155", marginBottom: "0.25rem" }}>
                  <span>Severity (S)</span>
                  <span style={{ color: "#0284c7" }}>{severity}/10</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={severity}
                  onChange={(e) => setSeverity(Number(e.target.value))}
                  style={{ width: "100%", accentColor: "#0284c7", cursor: "pointer" }}
                />
                <small style={{ color: "#94a3b8", fontSize: "0.62rem", display: "block", marginTop: "0.15rem" }}>
                  Impact on customer
                </small>
              </div>

              {/* Occurrence */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.72rem", fontWeight: 700, color: "#334155", marginBottom: "0.25rem" }}>
                  <span>Occurrence (O)</span>
                  <span style={{ color: "#0284c7" }}>{occurrence}/10</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={occurrence}
                  onChange={(e) => setOccurrence(Number(e.target.value))}
                  style={{ width: "100%", accentColor: "#0284c7", cursor: "pointer" }}
                />
                <small style={{ color: "#94a3b8", fontSize: "0.62rem", display: "block", marginTop: "0.15rem" }}>
                  Failure frequency
                </small>
              </div>

              {/* Detection */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.72rem", fontWeight: 700, color: "#334155", marginBottom: "0.25rem" }}>
                  <span>Detection (D)</span>
                  <span style={{ color: "#0284c7" }}>{detection}/10</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={detection}
                  onChange={(e) => setDetection(Number(e.target.value))}
                  style={{ width: "100%", accentColor: "#0284c7", cursor: "pointer" }}
                />
                <small style={{ color: "#94a3b8", fontSize: "0.62rem", display: "block", marginTop: "0.15rem" }}>
                  Ease of detection
                </small>
              </div>
            </div>
          </div>

          {/* Toggle card matching the sample screenshot */}
          <label className="cmts-job-form__toggle">
            <input
              type="checkbox"
              checked={syncFeedback}
              onChange={(e) => setSyncFeedback(e.target.checked)}
            />
            <span>
              <strong>Auto-sync Risk Feedback to Complaint and RCA Timeline</strong>
              <small>Propagate confirmed failure mode score directly into complaint risk registry and audit trail.</small>
            </span>
          </label>
        </div>
      </CmtsEnterpriseModal>
    </div>
  );
}
